import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { pdf } from "@react-pdf/renderer";
import InvoicePDF from "../components/InvoicePDF_v2";
import { saveAs } from "file-saver";
import DobropisPDF from "../components/DobropisPDF";
import VeleposlanistvoPDF from "../components/VeleposlanistvoPDF";
import PreInvoicePDF from "../components/PreInvoicePDF";
import StornoInvoicePDF from "../components/StornoInvoicePDF";
import { addDays, format } from "date-fns";
import { formatEUR, generatePDFName } from "../utils/utils";
import axios from "../axios";

const AutomaticInvoiceForm = ({
    initialData,
    selectedYear,
    resetAutomationForm,
    agents,
    billTypes,
    projects,
}) => {
    const [submittedFormIndices, setSubmittedFormIndices] = useState(new Set());
    const [invoiceForms, setInvoiceForm] = useState(
        initialData.map((initData) => ({
            invoiceType: initData.invoiceType,
            language: initData.language,
            date: initData.date,
            invoiceNumber: initData.invoiceNumber,
            reference: initData.reference,
            paymentDays: initData.paymentDays,
            company: initData.company,
            companyAddress: initData.companyAddress,
            companyCity: initData.companyCity,
            companyVAT: initData.companyVAT,
            companyEmail: initData.companyEmail,
            companyPhone: initData.companyPhone,
            propertyAddress: initData.propertyAddress,
            amount: initData.amount || 0,
            vatRate: initData.vatRate,
            discount: initData.discount,
            agent: initData.agent,
            billType: initData.billType,
            project: initData.project,
            description: initData.description,
            additional_services: initData.additional_services || 0,
        }))
    );
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [formError, setFormError] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const [downloadError, setDownloadError] = useState(null);

    // Calculate values for each invoice form
    const calculateInvoiceValues = useMemo(() => {
        return invoiceForms.map((invoiceForm) => {
            const amountWithoutVAT =
                invoiceForm.billType === "oddaja"
                    ? parseFloat(invoiceForm.amount) +
                          parseFloat(invoiceForm.additional_services) || 0
                    : parseFloat(invoiceForm.amount) || 0;

            const discountAmount =
                invoiceForm.discount === 0
                    ? 0
                    : amountWithoutVAT * (invoiceForm.discount / 100);

            const amountAfterDiscount = amountWithoutVAT - discountAmount;

            const vatAmount =
                invoiceForm.vatRate === 0
                    ? 0
                    : amountAfterDiscount * (invoiceForm.vatRate / 100);

            const totalWithVAT = amountAfterDiscount + vatAmount;

            return {
                amountWithoutVAT,
                discountAmount,
                amountAfterDiscount,
                vatAmount,
                totalWithVAT,
                discountValues: {
                    discountPercentage: invoiceForm.discount,
                    discountAmount: discountAmount,
                },
            };
        });
    }, [invoiceForms]);

    const handleInputChange = (index, e) => {
        const { name, value } = e.target;
        setInvoiceForm((prevForms) => {
            const updatedForms = [...prevForms];
            const updatedForm = {
                ...updatedForms[index],
                [name]: value,
            };

            // Auto-generate reference when date or invoice number changes
            if (name === "date" || name === "invoiceNumber") {
                const invoiceNumber =
                    name === "invoiceNumber"
                        ? value
                        : updatedForm.invoiceNumber;
                const date = name === "date" ? value : updatedForm.date;

                if (name === "invoiceNumber" && value.length === 0) {
                    updatedForm.reference = "SI00";
                } else if (invoiceNumber && date) {
                    const year = new Date(date).getFullYear();
                    updatedForm.reference = `SI00 ${invoiceNumber} ${year}`;
                }
            }

            updatedForms[index] = updatedForm;
            return updatedForms;
        });
    };

    const generatePdfBlob = async (invoiceForm) => {
        const today = new Date();

        // Ensure no time component — normalize to start of the day
        const startOfToday = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate()
        );

        const daysToAdd = Number(invoiceForm.paymentDays || 0);
        const newDate = addDays(startOfToday, daysToAdd);

        const paymentDate = format(newDate, "dd. MM. yyyy");

        // Find the index of this invoice form in the array
        const invoiceFormIndex = invoiceForms.findIndex(
            (form) =>
                form.invoiceNumber === invoiceForm.invoiceNumber &&
                form.date === invoiceForm.date
        );

        // Get calculated values for this specific invoice
        const {
            amountWithoutVAT,
            vatAmount,
            totalWithVAT,
            discountAmount,
            discountValues,
        } = calculateInvoiceValues[invoiceFormIndex];

        let PDFFile = null;

        switch (invoiceForm.invoiceType) {
            case "invoice":
                const invoicePdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                };
                PDFFile = <InvoicePDF data={invoicePdfData} />;
                break;
            case "preinvoice":
                const preInvoicePdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                };
                PDFFile = <PreInvoicePDF data={preInvoicePdfData} />;
                break;
            case "storno_invoice":
                const stornoInvoicePdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    companyName: invoiceForm.company,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                };
                PDFFile = <StornoInvoicePDF data={stornoInvoicePdfData} />;
                break;
            case "dobropis":
                const dobropisPdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    companyName: invoiceForm.company,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                };
                PDFFile = <DobropisPDF data={dobropisPdfData} />;
                break;
            case "veleposlanistvo":
                const veleposlanistvoPdfData = {
                    language: invoiceForm.language,
                    companyName: invoiceForm.company,
                    companyAddress: invoiceForm.companyAddress,
                    companyCity: invoiceForm.companyCity,
                    companyVAT: invoiceForm.companyVAT,
                    companyEmail: invoiceForm.companyEmail,
                    companyPhone: invoiceForm.companyPhone,
                    invoiceNumber: invoiceForm.invoiceNumber,
                    reference:
                        invoiceForm.reference || invoiceForm.invoiceNumber,
                    issueDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    serviceDate: new Date(invoiceForm.date).toLocaleDateString(
                        "sl-SI"
                    ),
                    propertyAddress:
                        invoiceForm.billType.length > 0
                            ? invoiceForm.propertyAddress
                            : null,
                    services: [
                        {
                            description: invoiceForm.description || "",
                            amount: amountWithoutVAT,
                        },
                    ],
                    amountWithoutVAT: amountWithoutVAT,
                    discountPercentage: discountValues.discountPercentage || 0,
                    discountAmount: discountValues.discountAmount || 0,
                    subtotal:
                        invoiceForm.billType === "oddaja"
                            ? amountWithoutVAT +
                              parseFloat(invoiceForm.additional_services)
                            : amountWithoutVAT,
                    vatRate: invoiceForm.vatRate,
                    vatAmount: vatAmount,
                    totalWithVAT: totalWithVAT,
                    paymentDays: invoiceForm.paymentDays,
                    paymentDate: paymentDate,
                    billType: invoiceForm.billType,
                    additional_services: invoiceForm.additional_services || 0,
                };
                PDFFile = <VeleposlanistvoPDF data={veleposlanistvoPdfData} />;
                break;
            default:
                PDFFile = null;
                break;
        }

        if (!PDFFile) {
            throw new Error("Napaka pri ustvarjanju PDF-ja.");
        }

        const blob = await pdf(PDFFile).toBlob();
        return blob;
    };

    const handleSubmitInvoice = async (e, invoiceIndex) => {
        e.preventDefault();
        setFormSubmitting(true);
        setFormError(null);

        const invoiceForm = invoiceForms[invoiceIndex];

        if (
            !invoiceForm.invoiceNumber ||
            !invoiceForm.date ||
            !invoiceForm.company ||
            !invoiceForm.amount ||
            !invoiceForm.agent
        ) {
            setFormError("Prosimo, izpolnite vsa obvezna polja");
            setFormSubmitting(false);
            return;
        }

        try {
            // Generate PDF blob with compression
            let pdfBlob = null;
            try {
                pdfBlob = await generatePdfBlob(invoiceForm);

                // Check blob size (limit to ~10MB for transmission)
                if (pdfBlob.size > 10 * 1024 * 1024) {
                    console.warn("PDF blob too large:", pdfBlob.size, "bytes");
                    setFormError("PDF je prevelik za nalaganje");
                    setFormSubmitting(false);
                    return;
                }

                // Convert blob to base64 for transmission
                const reader = new FileReader();
                const base64Promise = new Promise((resolve, reject) => {
                    reader.onload = () => resolve(reader.result);
                    reader.onerror = reject;
                    reader.ontimeout = () =>
                        reject(new Error("Timeout reading PDF"));
                });

                // Add timeout for large files
                setTimeout(() => {
                    if (reader.readyState === FileReader.LOADING) {
                        reader.abort();
                    }
                }, 30000); // 30 second timeout

                reader.readAsDataURL(pdfBlob);
                const base64Data = await base64Promise;

                console.log(
                    "PDF blob generated successfully, size:",
                    pdfBlob.size,
                    "bytes"
                );

                // Add blob to form data
                const formDataWithBlob = {
                    ...invoiceForm,
                    // blob: base64Data, // TODO: Uncomment when 100% ready for production
                };

                await axios.post(
                    `/api/racuni/${selectedYear}`,
                    formDataWithBlob,
                    {
                        timeout: 60000, // 60 second timeout for large uploads
                        maxContentLength: 50 * 1024 * 1024, // 50MB limit
                    }
                );

                toast.success("Račun uspešno dodan!");
            } catch (pdfError) {
                console.warn("Error generating PDF blob:", pdfError);
                // Show warning but continue without PDF
                toast.warn(
                    "PDF se ni mogel naložiti v oblak, račun je bil kljub temu dodan"
                );
                await axios.post(`/api/racuni/${selectedYear}`, invoiceForm);
            }

            // Mark this form as submitted
            const newSubmittedIndices = new Set(submittedFormIndices);
            newSubmittedIndices.add(invoiceIndex);
            setSubmittedFormIndices(newSubmittedIndices);

            // Only reset when all forms are submitted
            if (newSubmittedIndices.size === invoiceForms.length) {
                resetAutomationForm();
            }
        } catch (error) {
            console.error("Error submitting invoice:", error);
            setFormError("Napaka pri dodajanju računa. Poskusite znova.");
        } finally {
            setFormSubmitting(false);
        }
    };

    const handleDownload = async (index) => {
        const invoiceForm = invoiceForms[index];
        try {
            setDownloadError(null);
            setDownloading(true);

            const blob = await generatePdfBlob(invoiceForm);
            saveAs(
                blob,
                `${generatePDFName(
                    invoiceForm.company,
                    invoiceForm.invoiceNumber
                )}.pdf`
            );
        } catch (error) {
            console.error(error);
            setDownloadError("Napaka pri prenosu.");
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="p-6 mx-auto bg-white rounded-lg shadow-lg">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Račun</h2>
            </div>

            {/* FORM */}
            <div>
                {formError && (
                    <div className="flex items-center p-4 mb-6 text-red-800 bg-red-100 border border-red-200 rounded-md">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-5 h-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                            />
                        </svg>
                        {formError}
                    </div>
                )}

                {invoiceForms.map((invoiceForm, index) => {
                    // Skip rendering if this form has been submitted
                    if (submittedFormIndices.has(index)) {
                        return null;
                    }

                    const {
                        amountWithoutVAT,
                        vatAmount,
                        totalWithVAT,
                        discountAmount,
                        discountValues,
                    } = calculateInvoiceValues[index];

                    return (
                        <div
                            key={index}
                            className="p-6 mb-8 border border-gray-200 rounded-lg bg-gray-50 last-of-type:mb-0"
                        >
                            {invoiceForms.length > 1 && (
                                <div className="mb-6">
                                    <h3 className="text-lg font-semibold text-gray-800">
                                        Račun #{index + 1}
                                    </h3>
                                </div>
                            )}

                            <form
                                onSubmit={(e) => handleSubmitInvoice(e, index)}
                                className="space-y-8"
                            >
                                {/* Basic invoice information */}
                                <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                                    <div className="flex items-center mb-4 text-blue-700">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                            />
                                        </svg>
                                        <h4 className="leading-[1.1] text-lg font-medium">
                                            Osnovni podatki računa
                                        </h4>
                                    </div>
                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Tip dokumenta*
                                            </label>
                                            <select
                                                name="invoiceType"
                                                value={invoiceForm.invoiceType}
                                                onChange={(e) =>
                                                    handleInputChange(index, e)
                                                }
                                                className="w-full p-2 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="invoice">
                                                    Račun
                                                </option>
                                                <option value="preinvoice">
                                                    Predračun
                                                </option>
                                                <option value="dobropis">
                                                    Dobropis
                                                </option>
                                                <option value="storno_invoice">
                                                    Storno račun
                                                </option>
                                                <option value="veleposlanistvo">
                                                    Veleposlaništvo
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Jezik*
                                            </label>
                                            <select
                                                name="language"
                                                value={invoiceForm.language}
                                                onChange={(e) =>
                                                    handleInputChange(index, e)
                                                }
                                                className="w-full p-2 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                required
                                            >
                                                <option value="SI">
                                                    Slovenski
                                                </option>
                                                <option value="EN">
                                                    Angleški
                                                </option>
                                            </select>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Datum*
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="date"
                                                    name="date"
                                                    value={invoiceForm.date}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Številka računa*
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="invoiceNumber"
                                                    value={
                                                        invoiceForm.invoiceNumber
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Referenca
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="reference"
                                                    value={
                                                        invoiceForm.reference
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="SI00 številka"
                                                    readOnly
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Rok plačila (dni)
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="number"
                                                    name="paymentDays"
                                                    value={
                                                        invoiceForm.paymentDays
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    min="0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Client information */}
                                <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                                    <div className="flex items-center mb-4 text-blue-700">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                            />
                                        </svg>
                                        <h4 className="leading-[1.1] text-lg font-medium">
                                            Podatki o stranki
                                        </h4>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Podjetje/Ime*
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="company"
                                                    value={invoiceForm.company}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Naslov
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="companyAddress"
                                                    value={
                                                        invoiceForm.companyAddress
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Mesto in poštna številka
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="companyCity"
                                                    value={
                                                        invoiceForm.companyCity
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                ID za DDV / Davčna številka
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="companyVAT"
                                                    value={
                                                        invoiceForm.companyVAT
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                E-naslov
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        className="w-5 h-5 text-gray-400"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M16.5 12a4.5 4.5 0 1 1-9 0 4.5 4.5 0 0 1 9 0Zm0 0c0 1.657 1.007 3 2.25 3S21 13.657 21 12a9 9 0 1 0-2.636 6.364M16.5 12V8.25"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="companyEmail"
                                                    value={
                                                        invoiceForm.companyEmail
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="john@example.com"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Telefon
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        className="w-5 h-5 text-gray-400"
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="companyPhone"
                                                    value={
                                                        invoiceForm.companyPhone
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    placeholder="040123123"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Service information */}
                                <div className="p-5 rounded-lg shadow-sm bg-gray-50">
                                    <div className="flex items-center mb-4 text-blue-700">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                            />
                                        </svg>
                                        <h4 className="leading-[1.1] text-lg font-medium">
                                            Podatki o storitvi
                                        </h4>
                                    </div>

                                    <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Naslov nepremičnine
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="propertyAddress"
                                                    value={
                                                        invoiceForm.propertyAddress
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Tip računa
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                        className="size-6"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M9.568 3H5.25A2.25 2.25 0 0 0 3 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 0 0 5.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 0 0 9.568 3Z"
                                                        />
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M6 6h.008v.008H6V6Z"
                                                        />
                                                    </svg>
                                                </div>
                                                <select
                                                    name="billType"
                                                    value={invoiceForm.billType}
                                                    onChange={(e) => {
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        );
                                                        setInvoiceForm(
                                                            (prev) => ({
                                                                ...prev,
                                                                additional_services: 0,
                                                            })
                                                        );
                                                    }}
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    {billTypes.map((type) => (
                                                        <option
                                                            key={type.value}
                                                            value={type.value}
                                                        >
                                                            {type.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Izberite agenta
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        strokeWidth={1.5}
                                                        stroke="currentColor"
                                                        className="size-6"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z"
                                                        />
                                                    </svg>
                                                </div>
                                                <select
                                                    name="agent"
                                                    value={invoiceForm.agent}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    {agents.map((agent) => (
                                                        <option
                                                            key={agent.value}
                                                            value={agent.value}
                                                        >
                                                            {agent.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>

                                        {invoiceForm.agent === "STO" && (
                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Izberite projekt
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            width="16"
                                                            height="16"
                                                            fill="currentColor"
                                                            className="bi bi-house-gear"
                                                            viewBox="0 0 16 16"
                                                        >
                                                            <path d="M7.293 1.5a1 1 0 0 1 1.414 0L11 3.793V2.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v3.293l2.354 2.353a.5.5 0 0 1-.708.708L8 2.207l-5 5V13.5a.5.5 0 0 0 .5.5h4a.5.5 0 0 1 0 1h-4A1.5 1.5 0 0 1 2 13.5V8.207l-.646.647a.5.5 0 1 1-.708-.708z" />
                                                            <path d="M11.886 9.46c.18-.613 1.048-.613 1.229 0l.043.148a.64.64 0 0 0 .921.382l.136-.074c.561-.306 1.175.308.87.869l-.075.136a.64.64 0 0 0 .382.92l.149.045c.612.18.612 1.048 0 1.229l-.15.043a.64.64 0 0 0-.38.921l.074.136c.305.561-.309 1.175-.87.87l-.136-.075a.64.64 0 0 0-.92.382l-.045.149c-.18.612-1.048.612-1.229 0l-.043-.15a.64.64 0 0 0-.921-.38l-.136.074c-.561.305-1.175-.309-.87-.87l.075-.136a.64.64 0 0 0-.382-.92l-.148-.044c-.613-.181-.613-1.049 0-1.23l.148-.043a.64.64 0 0 0 .382-.921l-.074-.136c-.306-.561.308-1.175.869-.87l.136.075a.64.64 0 0 0 .92-.382zM14 12.5a1.5 1.5 0 1 0-3 0 1.5 1.5 0 0 0 3 0" />
                                                        </svg>
                                                    </div>
                                                    <select
                                                        name="project"
                                                        value={
                                                            invoiceForm.project
                                                        }
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                index,
                                                                e
                                                            )
                                                        }
                                                        className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    >
                                                        <option value=""></option>
                                                        {projects.map(
                                                            (project, key) => (
                                                                <option
                                                                    key={key}
                                                                    value={
                                                                        project.value
                                                                    }
                                                                >
                                                                    {
                                                                        project.value
                                                                    }
                                                                </option>
                                                            )
                                                        )}
                                                    </select>
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                {invoiceForm.billType ===
                                                "oddaja"
                                                    ? "Provizija brez DDV (€)*"
                                                    : "Znesek brez DDV (€)*"}
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="number"
                                                    name="amount"
                                                    value={invoiceForm.amount}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    step="0.01"
                                                    min="0"
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {invoiceForm.billType === "oddaja" && (
                                            <div>
                                                <label className="block mb-1 text-sm font-medium text-gray-700">
                                                    Del dodatnih storitev
                                                </label>
                                                <div className="relative">
                                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="w-5 h-5 text-gray-400"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                            />
                                                        </svg>
                                                    </div>
                                                    <input
                                                        type="number"
                                                        name="additional_services"
                                                        value={
                                                            invoiceForm.additional_services
                                                        }
                                                        onChange={(e) =>
                                                            handleInputChange(
                                                                index,
                                                                e
                                                            )
                                                        }
                                                        step="0.01"
                                                        min="0"
                                                        className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Stopnja DDV (%)
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                                        />
                                                    </svg>
                                                </div>
                                                <select
                                                    name="vatRate"
                                                    value={invoiceForm.vatRate}
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                >
                                                    <option value="0">
                                                        0%
                                                    </option>
                                                    <option value="9.5">
                                                        9.5%
                                                    </option>
                                                    <option value="22">
                                                        22%
                                                    </option>
                                                </select>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Popust
                                            </label>
                                            <div className="relative">
                                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                                                        />
                                                    </svg>
                                                </div>
                                                <input
                                                    type="text"
                                                    name="discount"
                                                    value={`${invoiceForm.discount}%`}
                                                    onChange={(e) => {
                                                        const raw =
                                                            e.target.value.replace(
                                                                "%",
                                                                ""
                                                            );
                                                        const numeric =
                                                            raw.replace(
                                                                /[^\d.]/g,
                                                                ""
                                                            );

                                                        // Convert to number and clamp between 0 and 100
                                                        const clamped =
                                                            Math.max(
                                                                0,
                                                                Math.min(
                                                                    100,
                                                                    parseFloat(
                                                                        numeric ||
                                                                            0
                                                                    )
                                                                )
                                                            );
                                                        setInvoiceForm({
                                                            ...invoiceForm,
                                                            discount: clamped,
                                                        });
                                                    }}
                                                    className="w-full h-[38px] p-2 pl-10 transition-colors bg-gray-100 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>

                                        <div className="md:col-span-2">
                                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                                Opis storitve
                                            </label>
                                            <div className="relative">
                                                <div className="absolute flex items-start pointer-events-none top-3 left-3">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 text-gray-400"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M4 6h16M4 12h16M4 18h7"
                                                        />
                                                    </svg>
                                                </div>
                                                <textarea
                                                    name="description"
                                                    value={
                                                        invoiceForm.description
                                                    }
                                                    onChange={(e) =>
                                                        handleInputChange(
                                                            index,
                                                            e
                                                        )
                                                    }
                                                    rows="3"
                                                    className="w-full p-2 pl-10 transition-colors border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Invoice Summary */}
                                <div className="p-5 border border-blue-100 rounded-lg shadow-sm bg-gradient-to-r from-blue-50 to-gray-50">
                                    <div className="flex items-center mb-4 text-blue-700">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-5 h-5 mr-2"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                                            />
                                        </svg>
                                        <h4 className="leading-[1.1] text-lg font-medium">
                                            Povzetek računa
                                        </h4>
                                    </div>

                                    <div className="p-5 bg-white border border-gray-200 rounded-md shadow-inner">
                                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                                            <span className="text-sm text-gray-700">
                                                Vrednost brez DDV:
                                            </span>
                                            <span className="text-base font-medium sm:text-lg min-w-[85px] text-right">
                                                {formatEUR(amountWithoutVAT)} €
                                            </span>
                                        </div>
                                        {discountValues.discountPercentage >
                                            0 &&
                                            discountValues.discountAmount >
                                                0 && (
                                                <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                                                    <span className="text-sm text-gray-700">
                                                        Popust (
                                                        {
                                                            discountValues.discountPercentage
                                                        }
                                                        %):
                                                    </span>
                                                    <span className="text-base font-medium sm:text-lg min-w-[85px] text-right">
                                                        {formatEUR(
                                                            discountValues.discountAmount
                                                        )}{" "}
                                                        €
                                                    </span>
                                                </div>
                                            )}
                                        <div className="flex items-center justify-between pb-3 mb-3 border-b border-gray-100">
                                            <span className="text-sm text-gray-700">
                                                DDV ({invoiceForm.vatRate}%):
                                            </span>
                                            <span className="text-base font-medium sm:text-lg min-w-[85px] text-right">
                                                {formatEUR(vatAmount)} €
                                            </span>
                                        </div>
                                        <div className="flex flex-col items-center justify-between pt-1 sm:flex-row">
                                            <span className="font-semibold text-gray-800">
                                                Skupaj za plačilo:
                                            </span>
                                            <span className="text-xl font-bold text-blue-700">
                                                {formatEUR(totalWithVAT)} €
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col items-center justify-between gap-4 pt-4 sm:flex-row">
                                    <button
                                        type="button"
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            await handleDownload(index);
                                        }}
                                        className={`flex items-center justify-center w-full px-6 py-3 font-medium text-white transition-colors rounded-lg shadow-md sm:w-auto hover:shadow-lg ${
                                            downloadError
                                                ? "bg-red-500 hover:bg-red-600"
                                                : "bg-gray-600 hover:bg-gray-700"
                                        }`}
                                    >
                                        {!downloadError && (
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="w-5 h-5 mr-2"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                                />
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                                />
                                            </svg>
                                        )}
                                        {downloadError
                                            ? "Napaka, poskusite znova"
                                            : downloading
                                            ? "Pripravljam račun..."
                                            : "Prenesi račun"}
                                    </button>

                                    {invoiceForm.invoiceType !==
                                        "storno_invoice" &&
                                        invoiceForm.invoiceType !==
                                            "dobropis" && (
                                            <div className="flex flex-col w-full gap-4 sm:flex-row sm:w-auto">
                                                <button
                                                    type="submit"
                                                    disabled={formSubmitting}
                                                    className={`w-full sm:w-auto px-6 py-3 rounded-lg font-medium text-white transition-colors flex items-center justify-center shadow-md hover:shadow-lg ${
                                                        formSubmitting
                                                            ? "bg-gray-400 cursor-not-allowed"
                                                            : "bg-blue-600 hover:bg-blue-700"
                                                    }`}
                                                >
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg"
                                                        className="w-5 h-5 mr-2"
                                                        fill="none"
                                                        viewBox="0 0 24 24"
                                                        stroke="currentColor"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                                                        />
                                                    </svg>
                                                    {formSubmitting
                                                        ? "Dodajanje..."
                                                        : "Dodaj račun"}
                                                </button>
                                            </div>
                                        )}
                                </div>
                            </form>
                        </div>
                    );
                })}
            </div>

            {/* Debug JSON (as in your original code) - REMOVE WHEN READY FOR PRODUCTION */}
            {/* <div className="p-4 mt-8 rounded-md bg-gray-50">
                <h4 className="mb-2 text-sm font-medium text-gray-700">
                    Current Form Data (JSON):
                </h4>
                <pre className="overflow-auto text-xs text-gray-600">
                    {JSON.stringify(invoiceForms, null, 2)}
                </pre>
            </div> */}
        </div>
    );
};

export default AutomaticInvoiceForm;
